import {motion} from "framer-motion";
import {
  Position,
  NodeToolbar,
  useReactFlow,
  Edge,
  useStore,
} from "@xyflow/react";
import {AnimatePresence} from "framer-motion";
import {Copy, Trash, GitBranchPlus} from "lucide-react";
import {FlowMode, FlowNodeTypes} from "@/types/node";
import {memo, useEffect, useState} from "react";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {useToast} from "@/hooks/useToast";
import {Icon} from "@/components/ui/icon";
import {capitalize} from "@/utils/index";
import {getNodeElement} from "@/utils/index";
import {useSave} from "@/contexts/SaveContext";
import {useCopyPasteContext} from "@/contexts/CopyPasteContext";
import {branchNodeBlueprint} from "@/pages/flow/NodeTypes/Blueprints/branch";
import useNode from "@/hooks/useNode";

const DEFAULT_OFFSET = 10;

const NodeWithToolbar = memo(function NodeWithToolbar({
  nodeId,
  nodeType,
  isToolbarVisible,
  onDelete,
  onMouseEnter,
  onMouseLeave,
}: {
  nodeId: string;
  nodeType: FlowNodeTypes;
  isToolbarVisible: boolean;
  onDelete: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const {toast} = useToast();
  const {handleSave, flowMode} = useSave();
  const {getEdges, getNode} = useReactFlow();
  const {copy} = useCopyPasteContext();
  const {addSiblingById} = useNode();

  const node = getNode(nodeId);
  const isBranchNode = nodeType === FlowNodeTypes.BRANCH;
  const isStartNode = nodeType === FlowNodeTypes.START;
  const isGlobalNode = nodeType === FlowNodeTypes.GLOBAL;
  const isEntryNode = nodeType === FlowNodeTypes.ENTRY;

  const zoom = useStore(state => state.transform[2]);
  const [toolbarOffset, setToolbarOffset] = useState(6);
  const position = isBranchNode ? Position.Right : Position.Top;
  const isLiveMode = flowMode === FlowMode.LIVE;

  function getChildren(
    nodeId: string,
    getEdges: () => Edge[],
    getNode: (value: string) => void,
  ) {
    return getEdges()
      .filter((e: Edge) => e.source === nodeId)
      .map((e: Edge) => getNode(e.target))
      .filter((node: any) => node && node.type !== FlowNodeTypes.PLACEHOLDER);
  }

  const parentEdge = getEdges().find(edge => edge.target === nodeId);
  const parentNode = parentEdge ? getNode(parentEdge.source) : null;

  const children = getChildren(nodeId, getEdges, getNode);
  const siblings = parentNode?.id
    ? getChildren(parentNode.id, getEdges, getNode)
    : [];

  const hasChildren = children.length > 0;
  const hasSiblings = siblings.length - 1 > 0;

  // Check if parent is a branch node
  const isParentBranch = parentNode?.type === FlowNodeTypes.BRANCH;

  // Check if parent is an entry node
  const isParentEntry = parentNode?.type === FlowNodeTypes.ENTRY;

  // Check if any child is a branch node
  const hasChildBranch = children.some(
    (child: any) => child?.type === FlowNodeTypes.BRANCH,
  );

  // Check if any child is a jump node
  const hasChildJump = children.some(
    (child: any) => child?.type === FlowNodeTypes.JUMP,
  );

  // Prevent deletion if:
  // 1. Current node is a branch with children and siblings, OR
  // 2. Parent is branch or entry and child is branch, OR
  // 3. Parent is entry and child is jump
  const isDeleteDisabled =
    (hasChildren && isBranchNode && hasSiblings) ||
    ((isParentBranch || isParentEntry) && hasChildBranch) ||
    (isParentEntry && hasChildJump);
  const nodeElement = getNodeElement(nodeId);
  const nodeElementWidth =
    (nodeElement?.querySelector(".branch-node") as HTMLElement)?.offsetWidth ||
    0;

  useEffect(() => {
    const newOffset = isBranchNode
      ? ((node.measured?.width - nodeElementWidth) / -2 + DEFAULT_OFFSET) * zoom
      : DEFAULT_OFFSET;

    setToolbarOffset(newOffset);
  }, [zoom, nodeElementWidth, node?.measured?.width, isBranchNode]);

  const handleAddSiblingBranch = () => {
    if (!parentNode || !isBranchNode) return;

    addSiblingById(nodeId, branchNodeBlueprint);
    handleSave();

    toast({
      icon: (
        <Icon
          size="tiny"
          variant="rounded"
          className="bg-green-500 border-0 shadow-toast-icon-green mr-3"
        >
          <GitBranchPlus className="text-slate-800 size-2.5 font-bold" />
        </Icon>
      ),
      title: "Branch Node Added",
    });
  };

  if (isStartNode || isGlobalNode || isEntryNode) return null;

  return (
    <NodeToolbar
      isVisible={isToolbarVisible && !isLiveMode}
      position={position}
      align={isBranchNode ? "start" : "end"}
      offset={toolbarOffset}
      onMouseEnter={onMouseEnter && onMouseEnter}
      onMouseLeave={onMouseLeave && onMouseLeave}
    >
      <div
        className={`hover-bridge z-0 absolute top-0 left-0 right-0 bottom-0 ${position === Position.Right ? "left-0" : "bottom-0"}`}
        style={{
          ...(position === Position.Right
            ? {left: `-${DEFAULT_OFFSET}px`}
            : {bottom: `-${DEFAULT_OFFSET}px`}),
        }}
      ></div>
      <AnimatePresence>
        <motion.div
          key="settings"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.1, ease: "easeInOut"}}
          className="z-10 flex relative"
        >
          {!isBranchNode && (
            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    copy(nodeId);
                    toast({
                      icon: (
                        <Icon
                          size="tiny"
                          variant="rounded"
                          className="bg-green-500 border-0 shadow-toast-icon-green mr-3"
                        >
                          <Copy className="text-slate-800 size-2.5 font-bold" />
                        </Icon>
                      ),
                      title: `${capitalize(nodeType)} Node Copied`,
                    });
                  }}
                  className="hover:bg-gray-100 bg-white shadow-node transition-colors flex w-9 h-9 items-center justify-center border rounded-md mr-1.5"
                >
                  <Copy size="18" className="text-label" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={10}>
                Copy
              </TooltipContent>
            </Tooltip>
          )}
          {isBranchNode && (
            <Tooltip delayDuration={0} disableHoverableContent={true}>
              <TooltipTrigger asChild>
                <button
                  onClick={handleAddSiblingBranch}
                  className="hover:bg-gray-100 bg-white shadow-node transition-colors flex w-9 h-9 items-center justify-center border rounded-md mr-1.5"
                >
                  <GitBranchPlus size="18" className="text-label" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={10}>
                New Condition
              </TooltipContent>
            </Tooltip>
          )}
          <Tooltip delayDuration={0} disableHoverableContent={true}>
            <TooltipTrigger asChild>
              <button
                onClick={() => {
                  onDelete();
                  handleSave();
                  toast({
                    icon: (
                      <Icon
                        size="tiny"
                        variant="rounded"
                        className="bg-red-500 border-0 shadow-toast-icon-red mr-3"
                      >
                        <Trash className="text-slate-800 size-2.5 font-bold" />
                      </Icon>
                    ),
                    title: `${capitalize(nodeType)} Node Deleted`,
                  });
                }}
                disabled={isDeleteDisabled}
                className={`${isDeleteDisabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"} bg-white shadow-node transition-colors flex w-9 h-9 items-center justify-center border rounded-md`}
              >
                <Trash size="18" className="text-red" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" sideOffset={10}>
              Remove
            </TooltipContent>
          </Tooltip>
        </motion.div>
      </AnimatePresence>
    </NodeToolbar>
  );
});

export default memo(NodeWithToolbar);
